import { WifiOff, ServerCrash, XCircle, SearchX, CircleAlert } from 'lucide-react';

export type ErrorType =
  | 'no-connection'
  | 'server-error'
  | 'purchase-failed'
  | 'empty-list'
  | 'generic';

interface ErrorScreenProps {
  errorType?: ErrorType;
  onRetry?: () => void;
  onClearFilters?: () => void;
  topBarHeight?: number;
}

const errorConfig = {
  'no-connection': {
    title: 'Você está sem internet',
    description: 'É só verificar sua conexão — a gente aparece de novo.',
    buttonText: 'Tentar novamente',
  },
  'server-error': {
    title: 'A loja tirou uma pausa',
    description: 'O problema é nosso. A gente já está resolvendo.',
    buttonText: 'Tentar novamente',
  },
  'purchase-failed': {
    title: 'Compra não concluída',
    description: 'Suas moedas estão seguras. O saldo não foi debitado.',
    buttonText: 'Tentar novamente',
  },
  'empty-list': {
    title: 'Nada por aqui agora',
    description:
      'Novos pedidos chegam o tempo todo. Ajuste os filtros pra ver mais.',
    buttonText: 'Explorar categorias',
  },
  'generic': {
    title: 'Ops, algo saiu errado',
    description: 'O problema é nosso. Se continuar, fala com o suporte.',
    buttonText: 'Tentar novamente',
  },
};

export function ErrorScreen({
  errorType = 'no-connection',
  onRetry,
  onClearFilters,
  topBarHeight = 60,
}: ErrorScreenProps) {
  const isNoConnection = errorType === 'no-connection';
  const config = errorConfig[errorType];

  const iconMap = {
    'no-connection': (
      <WifiOff size={52} color="#9CA3AF" strokeWidth={1.5} />
    ),
    'server-error': (
      <ServerCrash size={52} color="#9CA3AF" strokeWidth={1.5} />
    ),
    'purchase-failed': (
      <XCircle size={52} color="#9CA3AF" strokeWidth={1.5} />
    ),
    'empty-list': (
      <SearchX size={52} color="#9CA3AF" strokeWidth={1.5} />
    ),
    'generic': (
      <CircleAlert size={52} color="#9CA3AF" strokeWidth={1.5} />
    ),
  };

  const handleButtonClick = () => {
    if (errorType === 'empty-list' && onClearFilters) {
      onClearFilters();
      return;
    }

    if (errorType === 'no-connection') {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
      return;
    }

    if (onRetry) {
      onRetry();
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center w-full px-8"
      style={{
        backgroundColor: 'white',
        // Preenche toda a área disponível. Quando for "sem internet",
        // usa position: fixed para cobrir a tela inteira (incluindo filtros).
        paddingTop: '80px',
        paddingBottom: '80px',
        width: '100%',
        position: isNoConnection ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isNoConnection ? 50 : undefined,
      }}
    >
      <div className="flex flex-col items-center" style={{ gap: 0 }}>
        {/* Icon with pill background */}
        <div className="rounded-full bg-white p-4 shadow-sm mb-6">
          {iconMap[errorType]}
        </div>

        {/* Text group — title and description close together */}
        <div className="flex flex-col items-center gap-1 mb-6 px-2">
          <h2
            style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--foreground)',
              textAlign: 'center',
            }}
          >
            {config.title}
          </h2>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-normal)',
              color: 'var(--muted-foreground)',
              textAlign: 'center',
              maxWidth: '260px',
              lineHeight: '1.625',
            }}
          >
            {config.description}
          </p>
        </div>

        {/* Button - Secondary style */}
        <button
          onClick={handleButtonClick}
          className="w-full bg-white border shadow-sm transition-opacity active:opacity-80"
          style={{
            height: '48px',
            borderRadius: 'var(--radius-button)',
            borderColor: 'var(--border)',
            color: 'var(--foreground)',
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: 'pointer',
            maxWidth: '280px',
          }}
        >
          {config.buttonText}
        </button>
      </div>
    </div>
  );
}
